import type { GameState, TeamSelection } from './types'

const TEAM_GROUPS: (keyof TeamSelection)[] = [
  'backs',
  'halfBacks',
  'centre',
  'halfForwards',
  'forwards',
  'followers',
  'interchange',
]

export const selectedSeniorPlayerIds = (state: GameState): Set<string> => new Set(
  TEAM_GROUPS.flatMap(group => state.staffPlan.team[group].map(entry => entry.playerId)),
)

export const isSeniorSelected = (state: GameState, playerId: string): boolean =>
  selectedSeniorPlayerIds(state).has(playerId)

/**
 * Resolve a scripted staff-lineup name to the player currently occupying
 * that canonical slot. Match reports must follow submitted stable player IDs,
 * not the authored player who originally occupied the position.
 */
export const selectedPlayerForStaffSlot = (state: GameState, staffPlayerName: string): string => {
  const staffPlayer = state.club.players.find(player => player.name === staffPlayerName)
  if (!staffPlayer) return staffPlayerName

  const selectedIds = selectedSeniorPlayerIds(state)
  if (selectedIds.has(staffPlayer.id)) return staffPlayer.name

  const baseline = state.week?.staffDefaults?.team
  if (baseline) {
    for (const group of TEAM_GROUPS) {
      const index = baseline[group].findIndex(entry => entry.playerId === staffPlayer.id)
      if (index < 0) continue
      const replacementId = state.staffPlan.team[group][index]?.playerId
      const replacement = state.club.players.find(player => player.id === replacementId)
      if (replacement && selectedIds.has(replacement.id)) return replacement.name
    }
  }

  const fallback = state.club.players.find(player => selectedIds.has(player.id))
  return fallback?.name ?? staffPlayerName
}

export const reconcileStoredMatchSelection = (state: GameState): GameState => {
  if (!state.matchResult) return state
  const selectedIds = selectedSeniorPlayerIds(state)
  const remapName = (name: string) => {
    const player = state.club.players.find(entry => entry.name === name)
    return player && !selectedIds.has(player.id) ? selectedPlayerForStaffSlot(state, name) : name
  }
  const remapDescription = (description: string, from: string, to: string) => {
    if (from === to) return description
    const fromSurname = from.split(' ').at(-1) ?? from
    const toSurname = to.split(' ').at(-1) ?? to
    return description.replaceAll(from, to).replaceAll(fromSurname, toSurname)
  }

  return {
    ...state,
    matchResult: {
      ...state.matchResult,
      playerPerformances: state.matchResult.playerPerformances.map(performance => {
        const player = remapName(performance.player)
        return { ...performance, player, notes: remapDescription(performance.notes, performance.player, player) }
      }),
      keyEvents: state.matchResult.keyEvents.map(event => {
        const player = remapName(event.player)
        return { ...event, player, description: remapDescription(event.description, event.player, player) }
      }),
    },
  }
}
